import React from 'react';
import { createRoot } from 'react-dom/client';
import { ChatWidget } from '../components/chat/ChatWidget';
import { ErrorBoundary } from '../components/chat/ErrorBoundary';
import '../components/chat/i18n';
import { usePerformanceMonitoring } from '../components/chat/usePerformanceMonitoring';
import './widget.css';

// Global config storage
let widgetConfig: any = null;

const normalizeBaseUrl = (value?: unknown) => {
  if (typeof value !== 'string') return undefined;
  return value.endsWith('/') ? value.slice(0, -1) : value;
};

const deriveAssetsBaseUrl = (config: any) => {
  const direct = normalizeBaseUrl(config?.assets_base_url || config?.assetsBaseUrl);
  if (direct) return direct;

  const fromWidgetUrl = config?.widget_bundle_url || config?.widget_css_url;
  if (typeof fromWidgetUrl !== 'string') return undefined;

  try {
    const u = new URL(fromWidgetUrl);
    u.pathname = u.pathname.replace(/\/(widget\.js|widget\.css)$/, '');
    return normalizeBaseUrl(u.toString());
  } catch {
    return undefined;
  }
};

const ensureMountEl = (mountId?: string) => {
  const id = mountId && typeof mountId === 'string' ? mountId : 'dreambot-widget-root';

  const existing = document.getElementById(id);
  if (existing) return existing;

  const el = document.createElement('div');
  el.id = id;
  document.body.appendChild(el);
  return el;
};

const hexToHsl = (hex: string): string => {
  // Remove hash if present
  hex = hex.replace(/^#/, '');

  // Parse r, g, b
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // Normalize
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  // Convert to CSS HSL values (degrees, percentage, percentage) - strictly space separated for Tailwind opacity modifier compatibility
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

const ensureCustomStyle = () => {
  const id = 'dreambot-custom-theme-style';
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('style');
    el.id = id;
    document.head.appendChild(el);
  }
  return el;
};

const applyTheme = (el: HTMLElement, config?: any) => {
  el.classList.add('dreambot');

  // Apply theme from config
  const themeId = config?.widget_theme || 'default';
  el.setAttribute('data-theme', themeId);

  if (themeId === 'custom') {
    let tc = config?.theme_config || {};
    // Safety: Handle stringified JSON from DB if type mismatch occurs
    if (typeof tc === 'string') {
      try { tc = JSON.parse(tc); } catch (e) { console.error('Theme config parse error', e); tc = {}; }
    }

    const styleEl = ensureCustomStyle();

    // Helper: Hex handling
    const safeHexIdx = (val: string, fallback: string) => {
      if (!val) return fallback;
      if (val.match(/^#[0-9A-Fa-f]{6}$/)) return val;
      const match = val.match(/#[0-9A-Fa-f]{6}/);
      return match ? match[0] : fallback;
    };

    const getLuminance = (hex: string) => {
      const c = hex.replace(/^#/, '');
      const r = parseInt(c.substring(0, 2), 16) / 255;
      const g = parseInt(c.substring(2, 4), 16) / 255;
      const b = parseInt(c.substring(4, 6), 16) / 255;
      return 0.2126 * r + 0.7152 * g + 0.0722 * b;
    };

    // Base Colors
    const primary = safeHexIdx(tc.primary_color, '#000000');
    const bg = safeHexIdx(tc.widget_bg, '#ffffff');
    const text = safeHexIdx(tc.widget_text, '#000000');
    const userBg = safeHexIdx(tc.user_msg_bg, '#f3f4f6');
    const userText = tc.user_msg_text || '#000000';

    // Derived Variables for Premium Feel
    const ring = primary;
    const secondary = primary;
    const muted = userBg;

    // Auto-contrast border for inputs
    const isDarkBg = getLuminance(bg) < 0.5;
    const borderColor = isDarkBg ? '#374151' : '#e5e7eb'; // gray-700 : gray-200

    // Confetti Colors (derived from brand or gold/silver defaults)
    const confetti1 = primary;
    const confetti2 = safeHexIdx(tc.header_bg, '#FFD700');
    const confetti3 = '#ffffff';

    // Extended Styling
    const borderRadius = tc.border_radius ? parseInt(tc.border_radius) : 20;
    const fontFamily = tc.font_family || 'system';

    // Font family map
    const fontFamilyMap: Record<string, string> = {
      'system': "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      'inter': "'Inter', sans-serif",
      'roboto': "'Roboto', sans-serif",
      'poppins': "'Poppins', sans-serif",
      'outfit': "'Outfit', sans-serif",
      'playfair': "'Playfair Display', serif",
      'dm-sans': "'DM Sans', sans-serif",
      'space-grotesk': "'Space Grotesk', sans-serif"
    };
    const fontStack = fontFamilyMap[fontFamily] || fontFamilyMap['system'];

    // Inject Google Font if not system
    if (fontFamily !== 'system') {
      const googleFontId = 'dreambot-google-font';
      if (!document.getElementById(googleFontId)) {
        const link = document.createElement('link');
        link.id = googleFontId;
        link.rel = 'stylesheet';
        const fontName = fontFamily.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('+');
        link.href = `https://fonts.googleapis.com/css2?family=${fontName}:wght@400;500;600;700&display=swap`;
        document.head.appendChild(link);
      }
    }

    const css = `
      .dreambot[data-theme="custom"] {
        /* Core Colors */
        --primary: ${hexToHsl(primary)};
        --primary-foreground: 0 0% 100%;
        --background: ${hexToHsl(bg)};
        --foreground: ${hexToHsl(text)};
        --card: ${hexToHsl(bg)};
        --card-foreground: ${hexToHsl(text)};
        --popover: ${hexToHsl(bg)};
        --popover-foreground: ${hexToHsl(text)};
        --muted: ${hexToHsl(muted)};
        --muted-foreground: ${hexToHsl(text)};
        --accent: ${hexToHsl(muted)};
        --accent-foreground: ${hexToHsl(text)};
        --destructive: 0 84.2% 60.2%;
        --destructive-foreground: 0 0% 98%;
        --border: ${hexToHsl(borderColor)};
        --input: ${hexToHsl(borderColor)};
        --ring: ${hexToHsl(ring)};
        --secondary: ${hexToHsl(secondary)};
        --secondary-foreground: 0 0% 100%;

        /* Gradients & Raw Values */
        --gradient-primary: ${tc.header_bg || primary};
        --bot-message-gradient: ${tc.bot_msg_bg || primary};
        --user-message-bg: ${userBg};
        --user-message-text: ${userText};

        /* Confetti */
        --confetti-color-1: ${confetti1};
        --confetti-color-2: ${confetti2};
        --confetti-color-3: ${confetti3};

        /* Extended Styling */
        --custom-radius: ${borderRadius}px;
        --radius: ${Math.min(borderRadius / 20, 1) * 0.5}rem;
        font-family: ${fontStack} !important;

        /* Force solid background to prevent transparency issues */
        background-color: ${bg} !important;
        background: ${bg} !important;
      }

      /* === Premium Overrides === */
      
      /* Safety Overrides for Tailwind Utilities in Custom Theme */
      .dreambot[data-theme="custom"] .bg-background { background-color: ${bg} !important; }
      .dreambot[data-theme="custom"] .bg-card { background-color: ${bg} !important; }
      .dreambot[data-theme="custom"] .bg-popover { background-color: ${bg} !important; }
      .dreambot[data-theme="custom"] .bg-muted { background-color: ${muted} !important; }
      .dreambot[data-theme="custom"] .bg-accent { background-color: ${muted} !important; }
      
      /* 1. Message Bubbles - HARD RESET to prevent Glassmorphism leaks */
      .dreambot[data-theme="custom"] .message-bubble.user {
        background-color: ${userBg} !important;
        background: ${userBg} !important;
        color: ${userText} !important;
        backdrop-filter: none !important;
        -webkit-backdrop-filter: none !important;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
        border: 1px solid ${borderColor} !important;
        border-radius: ${Math.min(borderRadius, 18)}px !important;
      }
      .dreambot[data-theme="custom"] .message-bubble.bot {
        background: ${tc.bot_msg_bg || primary} !important;
        color: ${tc.bot_msg_text || '#ffffff'} !important;
        backdrop-filter: none !important;
        -webkit-backdrop-filter: none !important;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
        border: 1px solid transparent !important;
        border-radius: ${Math.min(borderRadius, 18)}px !important;
      }

      /* 2. Primary Elements */
      .dreambot[data-theme="custom"] .bg-primary,
      .dreambot[data-theme="custom"] .send-button {
         background: ${tc.header_bg || primary} !important;
         color: #ffffff !important;
      }
      .dreambot[data-theme="custom"] .text-primary-foreground {
         color: #ffffff !important;
      }

      /* 3. Floating Button Glow & Border */
      .dreambot[data-theme="custom"] .floating-button {
        border-color: ${primary} !important;
        border-radius: ${Math.min(borderRadius + 4, 22)}px !important;
      }
      .dreambot[data-theme="custom"] .floating-button-stroke {
        animation: float-gentle 3s ease-in-out infinite,
                   theme-glow-light 2.5s ease-in-out infinite;
      }

      /* 4. Scrollbars (Premium Touch) */
      .dreambot[data-theme="custom"] ::-webkit-scrollbar-thumb {
        background: ${primary}80; /* Alpha 0.5 */
        border-radius: 99px;
      }
      .dreambot[data-theme="custom"] ::-webkit-scrollbar-thumb:hover {
        background: ${primary}b3; /* Alpha 0.7 */
      }

      /* 5. Input Focus Rings */
      .dreambot[data-theme="custom"] input:not(.floating-composer-input):focus {
        border-color: ${primary} !important;
        box-shadow: 0 0 0 3px ${primary}1a !important; /* Alpha 0.1 */
      }
      
      /* 6. Product Cards */
      .dreambot[data-theme="custom"] .product-card:hover {
        border-color: ${primary} !important;
        box-shadow: 0 8px 16px ${primary}26 !important; /* Alpha 0.15 */
      }

      /* 7. Quick Replies */
      .dreambot[data-theme="custom"] .quick-reply-button:hover,
      .dreambot[data-theme="custom"] .response-button:hover {
        background: ${tc.header_bg || primary} !important;
        color: white !important;
        border-color: transparent !important;
      }

      /* 8. Widget Container Radius */
      .dreambot[data-theme="custom"] .widget-container {
        border-radius: ${borderRadius}px !important;
      }

      /* 9. Typography */
      .dreambot[data-theme="custom"] * {
        font-family: ${fontStack} !important;
      }
    `;
    styleEl.textContent = css;
  }

  // Apply light/dark mode
  try {
    const stored = localStorage.getItem('dreambot_theme') || localStorage.getItem('theme');
    const defaultMode = config?.widget_default_mode || 'light';
    const mode = stored === 'light' || stored === 'dark' ? stored : defaultMode;

    if (mode === 'dark') {
      el.classList.add('dark');
    } else {
      el.classList.remove('dark');
    }
  } catch {
    // Apply default mode from config if localStorage fails
    const defaultMode = config?.widget_default_mode || 'light';
    if (defaultMode === 'dark') {
      el.classList.add('dark');
    }
  }
};

const mount = (config: any) => {
  // Store config globally for runtime access
  widgetConfig = config;

  // Make config available to components via window
  const mountId = (config?.mount_id && typeof config.mount_id === 'string') ? config.mount_id : 'dreambot-widget-root';
  const apiBaseUrl = (config?.worker_base_url && typeof config.worker_base_url === 'string')
    ? config.worker_base_url
    : ((config?.api_base_url && typeof config.api_base_url === 'string') ? config.api_base_url : undefined);
  const assetsBaseUrl = deriveAssetsBaseUrl(config);

  (window as any).DreamBotConfig = {
    siteId: config.site_id,
    apiBaseUrl,
    assetsBaseUrl,
    mountId,
    businessName: config.business_name,
    brandTone: config.brand_tone,
    supportHours: config.support_hours,
    supportUrl: config.support_url,
    faq: config.faq,
    pluginVersion: config.plugin_version,
    widgetVersion: config.widget_version,
    environment: config.environment,
  };

  const el = ensureMountEl(mountId);
  applyTheme(el, config);

  function App() {
    // Track performance metrics
    usePerformanceMonitoring();

    return (
      <ErrorBoundary>
        <ChatWidget />
      </ErrorBoundary>
    );
  }

  createRoot(el).render(<App />);
};

// Export global API for loader
export const init = (config: any) => {
  if (!config) {
    console.error('[DreamBot Widget] init() called without config');
    return;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(config));
  } else {
    mount(config);
  }
};
